import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { register, verifyEmail, verifyGroupCode, clearRegistrationData } from '../actions/authActions';
import { getTermsByProgram } from '../actions/termsConditionsActions';
import { clearErrors } from '../actions/errorActions';
import { Target, TrendingUp, Lightbulb, Rocket } from 'lucide-react';
import EmailStep from '../components/auth/registration/EmailStep';
import TermsStep from '../components/auth/registration/TermsStep';
import UserDetailsStep from '../components/auth/registration/UserDetailsStep';

const RegisterPage = ({
  auth: { isAuthenticated },
  error,
  registrationData,
  registrationTerms,
  termsFetched,
  register,
  verifyEmail,
  verifyGroupCode,
  getTermsByProgram,
  clearErrors,
  clearRegistrationData
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearErrors();
      clearRegistrationData();
    };
  }, []);

  useEffect(() => {
    if (registrationData.noInvitation) {
      setLoading(false);
      setMsg(null);
    }
  }, [registrationData.noInvitation]);

  useEffect(() => {
    if (error.id === 'REGISTER_FAIL' || error.id === 'VERIFY_EMAIL_FAIL') {
      const raw = typeof error.msg === 'string' ? error.msg : error.msg?.msg || error.msg?.errors;
      const m = Array.isArray(raw)
        ? raw.map(e => (typeof e === 'object' ? e.msg : e)).join(', ')
        : raw || 'Operation failed';
      if (!registrationData.noInvitation) {
        setMsg(m);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } else {
      setMsg(null);
    }
  }, [error]);

  // Advance steps when email verified
  useEffect(() => {
    if (registrationData.email && !registrationData.noInvitation && currentStep === 1) {
      if (registrationData.isAdmin) {
        setCurrentStep(3);
      } else if (registrationData.program?._id) {
        getTermsByProgram(registrationData.program._id);
        setCurrentStep(2);
      } else {
        // No program — skip terms step
        setCurrentStep(3);
      }
      setMsg(null);
      setLoading(false);
    }
  }, [registrationData]);

  // Skip terms step if terms were fetched but none exist
  useEffect(() => {
    if (currentStep === 2 && termsFetched && !registrationTerms) {
      setCurrentStep(3);
    }
  }, [currentStep, termsFetched, registrationTerms]);

  const handleEmailVerification = (email) => {
    setMsg(null);
    setLoading(true);
    verifyEmail(email);
  };

  const handleGroupCodeVerification = (email, groupCode) => {
    setMsg(null);
    setLoading(true);
    verifyGroupCode(email, groupCode);
  };

  const handleTermsAccept = () => {
    setCurrentStep(3);
    setMsg(null);
  };

  const handleRegistration = (userData) => {
    setMsg(null);
    setLoading(true);
    const payload = registrationData.viaGroupCode
      ? { ...userData, groupCode: registrationData.group?.code }
      : userData;
    register(payload);
  };

  const handleSkipGroupCode = () => {
    setMsg('You need either an invitation or a valid group code to register.');
    setLoading(false);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setMsg(null);
    setLoading(false);
    clearRegistrationData();
  };

  const handleBackToStep2 = () => {
    if (registrationData.isAdmin) {
      setCurrentStep(1);
      setMsg(null);
      setLoading(false);
    } else {
      setCurrentStep(2);
      setMsg(null);
      setLoading(false);
    }
  };

  const isAdmin = registrationData?.isAdmin;
  const totalSteps = isAdmin ? 2 : 3;

  const getDisplayStep = () => {
    if (isAdmin) return currentStep === 3 ? 2 : 1;
    return currentStep;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailStep
            onVerifyEmail={handleEmailVerification}
            onVerifyGroupCode={handleGroupCodeVerification}
            onSkipGroupCode={handleSkipGroupCode}
            error={msg}
            loading={loading}
            noInvitation={registrationData.noInvitation}
            email={registrationData.email}
          />
        );
      case 2:
        return (
          <TermsStep
            termsConditions={registrationTerms}
            onAccept={handleTermsAccept}
            onBack={handleBackToStep1}
            loading={!registrationTerms}
          />
        );
      case 3:
        return (
          <UserDetailsStep
            onRegister={handleRegistration}
            onBack={handleBackToStep2}
            error={msg}
            loading={loading}
            email={registrationData.email}
          />
        );
      default:
        return null;
    }
  };

  const features = [
    { icon: Target,     text: 'Set and track your learning goals' },
    { icon: TrendingUp, text: 'Monitor your progress in real time' },
    { icon: Lightbulb,  text: 'Access courses built for your path' },
    { icon: Rocket,     text: 'Achieve more with structured learning' },
  ];

  return (
    <div className="min-h-screen flex">

      {/* Left — Branding Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#054581] to-[#1086a4] flex-col justify-between p-12 text-white">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Your Big Year</h1>
          <p className="text-white/70 text-sm">Learning Management System</p>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-semibold leading-snug">
            Start your journey<br />towards your goals.
          </h2>
          <div className="space-y-3 pt-2">
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-2 shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm text-white/90">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/50 text-xs">powered by Your Big Year®</p>
      </div>

      {/* Right — Form Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-bold text-[#054581]">Your Big Year</h1>
            <p className="text-gray-500 text-sm">Learning Management System</p>
          </div>

          {/* Header + step indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-[#054581]">Create account</h2>
                <p className="text-gray-500 mt-1">Step {getDisplayStep()} of {totalSteps}</p>
              </div>
              <div className="flex gap-1.5">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
                  <div
                    key={step}
                    className={`h-2 w-8 rounded-full transition-colors duration-300 ${
                      step <= getDisplayStep() ? 'bg-[#1086a4]' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {renderStep()}

          {currentStep === 1 && (
            <p className="mt-6 text-center text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  error: state.error,
  registrationData: state.auth.registrationData,
  registrationTerms: state.termsConditions.registrationTerms,
  termsFetched: state.termsConditions.termsFetched
});

export default connect(mapStateToProps, {
  register, verifyEmail, verifyGroupCode, getTermsByProgram, clearErrors, clearRegistrationData
})(RegisterPage);
